import React, { useCallback, useEffect, useRef } from 'react'

import { Box, Display, Overflow } from '@gousto-internal/citrus-react'
import type { EmblaOptionsType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'

type CarouselProps = {
  carouselAreaLabel: string
  children: React.ReactNode
  options?: EmblaOptionsType
  slideWidth?: string
}

export const Carousel = ({ carouselAreaLabel, children, options, slideWidth }: CarouselProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(options)
  const viewportRef = useRef<HTMLDivElement | null>(null)

  const setViewportRef = useCallback(
    (node: HTMLDivElement | null) => {
      viewportRef.current = node
      emblaRef(node)
    },
    [emblaRef],
  )

  const handleWheel = useCallback(
    (event: WheelEvent) => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return

      const horizontalDelta = event.deltaX

      if (!emblaApi || horizontalDelta === 0) return

      event.preventDefault()
      event.stopPropagation()

      if (horizontalDelta > 0) {
        emblaApi.scrollNext()
      } else {
        emblaApi.scrollPrev()
      }
    },
    [emblaApi],
  )

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return undefined

    viewport.addEventListener('wheel', handleWheel, { passive: false })

    return () => {
      viewport.removeEventListener('wheel', handleWheel)
    }
  }, [handleWheel])

  const reInit = useCallback(() => {
    if (!emblaApi) return

    emblaApi.reInit()
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return undefined

    emblaApi.on('resize', reInit)

    return () => {
      emblaApi.off('resize', reInit)
    }
  }, [emblaApi, reInit])

  return (
    <Box
      className="embla"
      overflowX={Overflow.Hidden}
      overflowY={Overflow.Hidden}
      style={{ overscrollBehaviorX: 'contain' }}
    >
      <Box
        className="embla__viewport"
        ref={setViewportRef}
        style={{ overscrollBehaviorX: 'contain' }}
      >
        <Box
          aria-label={carouselAreaLabel}
          aria-live="polite"
          aria-atomic="true"
          aria-roledescription="carousel"
          className="embla__container"
          display={Display.Flex}
          role="region"
          style={{ gap: '1rem' }}
          tabIndex={0}
        >
          {React.Children.map(children, (child, index) => (
            <Box
              aria-label={`${index + 1} of ${React.Children.count(children)}`}
              aria-roledescription={`Slide ${index + 1} of ${React.Children.count(children)}`}
              className="embla__slide"
              data-testid="carousel-slide"
              flexBasis={slideWidth}
              flexGrow={0}
              flexShrink={0}
              key={index}
              role="group"
              style={{ minWidth: 0 }}
              tabIndex={-1}
            >
              {child}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}
